<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useArrowsAiAssistants } from 'dashboard/composables/useArrowsAiAssistants';
import { useArrowsAiTools } from 'dashboard/composables/useArrowsAiTools';
import PageLayout from 'dashboard/components-next/arrowsAi/PageLayout.vue';
import MissingAssistant from 'dashboard/components-next/arrowsAi/MissingAssistant.vue';
import ToolCard from 'dashboard/components-next/arrowsAi/assistant/ToolCard.vue';
import CreateToolDialog from 'dashboard/components-next/arrowsAi/assistant/CreateToolDialog.vue';

const { t } = useI18n();
const route = useRoute();
const { getAssistant, rememberAssistant } = useArrowsAiAssistants();
const assistantId = computed(() => Number(route.params.assistantId));
const assistant = computed(() => {
  const current = getAssistant(assistantId.value);
  if (current) rememberAssistant(current.id);
  return current;
});
const { tools, createTool, updateTool, deleteTools } =
  useArrowsAiTools(assistantId);
const dialogRef = ref(null);
const selectedTool = ref({});

const openCreate = () => {
  selectedTool.value = {};
  nextTick(() => dialogRef.value.dialogRef.open());
};

const openEdit = tool => {
  selectedTool.value = tool;
  nextTick(() => dialogRef.value.dialogRef.open());
};

const validPayload = payload => {
  if (
    !payload.name.trim() ||
    !payload.description.trim() ||
    !payload.endpoint.trim().toLowerCase().startsWith('https://')
  ) {
    return false;
  }

  if (payload.authType === 'none') return true;
  if (payload.authType === 'api_key' && !payload.authHeader.trim()) {
    return false;
  }

  return Boolean(payload.authToken.trim() || selectedTool.value.authConfigured);
};

const handleSubmit = async payload => {
  if (!validPayload(payload)) {
    useAlert(t('ARROWS_AI.TOOLS.ERROR'));
    return;
  }

  try {
    if (selectedTool.value.id) await updateTool(selectedTool.value.id, payload);
    else await createTool(payload);
    useAlert(t('ARROWS_AI.TOOLS.SUCCESS', { name: payload.name.trim() }));
    dialogRef.value.dialogRef.close();
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.TOOLS.SAVE_ERROR'));
  }
};

const handleDelete = async id => {
  try {
    await deleteTools([id]);
    useAlert(t('ARROWS_AI.TOOLS.DELETED'));
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.TOOLS.SAVE_ERROR'));
  }
};
</script>

<template>
  <PageLayout
    :header-title="t('ARROWS_AI.TOOLS.TITLE')"
    :button-label="t('ARROWS_AI.TOOLS.ADD')"
    @click="openCreate"
  >
    <MissingAssistant v-if="!assistant" />
    <div v-else class="flex flex-col gap-6">
      <p class="text-sm text-n-slate-11">
        {{ t('ARROWS_AI.TOOLS.DESCRIPTION') }}
      </p>
      <div class="flex flex-col gap-3">
        <ToolCard
          v-for="tool in tools"
          :key="tool.id"
          :tool="tool"
          @edit="openEdit"
          @delete="handleDelete"
        />
        <p v-if="!tools.length" class="text-sm text-n-slate-11">
          {{ t('ARROWS_AI.TOOLS.EMPTY') }}
        </p>
      </div>
    </div>
    <CreateToolDialog
      ref="dialogRef"
      :tool="selectedTool"
      :assistant-id="assistantId"
      @submit="handleSubmit"
    />
  </PageLayout>
</template>
